/* ============================================
   AKI — POPUPS
   Modal shell, email capture (timer + exit intent),
   post-add-to-cart upsell, social proof toast.

   Every popup respects a frequency cap stored in
   localStorage so a returning visitor is not nagged.
   ============================================ */
(function () {
  'use strict';

  var STORE_PREFIX = 'aki:popup:';

  /* ------------------------------------------
     Frequency memory
     ------------------------------------------ */
  var Memory = {
    read: function (key) {
      try { return JSON.parse(window.localStorage.getItem(STORE_PREFIX + key) || 'null'); }
      catch (e) { return null; }
    },
    write: function (key, value) {
      try { window.localStorage.setItem(STORE_PREFIX + key, JSON.stringify(value)); }
      catch (e) { /* private mode — popup simply shows again next visit */ }
    },
    /* days = 0 means "once per session" */
    isSuppressed: function (key, days) {
      var rec = this.read(key);
      if (!rec || !rec.at) return false;
      if (rec.dismissedForever) return true;
      if (!days) return !!window.sessionStorage.getItem(STORE_PREFIX + key);
      var elapsed = Date.now() - rec.at;
      return elapsed < days * 86400000;
    },
    suppress: function (key, forever) {
      this.write(key, { at: Date.now(), dismissedForever: !!forever });
      try { window.sessionStorage.setItem(STORE_PREFIX + key, '1'); } catch (e) {}
    }
  };

  /* ------------------------------------------
     Focus trap (shared with drawers)
     ------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function trapFocus(container, onEscape) {
    function handle(e) {
      if (e.key === 'Escape') { onEscape(); return; }
      if (e.key !== 'Tab') return;
      var items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(function (el) {
        return el.offsetParent !== null;
      });
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    document.addEventListener('keydown', handle);
    return function () { document.removeEventListener('keydown', handle); };
  }

  /* Only one popup may be on screen at a time */
  var openPopup = null;

  /* ==========================================
     <aki-modal> — base shell
     ========================================== */
  class AkiModal extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.panel = this.querySelector('[data-modal-panel]');
      this.storageKey = this.dataset.key || this.id;
      this.release = null;
      this.opener = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-modal-close]') || e.target.closest('[data-modal-backdrop]')) {
          e.preventDefault();
          this.close(e.target.closest('[data-modal-decline]') ? 'declined' : 'closed');
        }
      });
    }

    canOpen() {
      if (openPopup) return false;
      if (window.Shopify && window.Shopify.designMode) return true;
      var days = Number(this.dataset.frequency || 7);
      return !Memory.isSuppressed(this.storageKey, days);
    }

    open(opener) {
      if (this.hasAttribute('data-open') || openPopup) return;
      openPopup = this;
      this.opener = opener || null;
      this.setAttribute('data-open', 'true');
      document.body.classList.add('is-locked');
      this.release = trapFocus(this.panel || this, () => this.close('closed'));

      window.requestAnimationFrame(() => {
        var focusTarget = this.querySelector('[data-modal-autofocus]') || this.querySelector('[data-modal-close]');
        if (focusTarget) focusTarget.focus();
      });

      this.dispatchEvent(new CustomEvent('aki:modal:open', { bubbles: true }));
    }

    close(reason) {
      if (!this.hasAttribute('data-open')) return;
      this.removeAttribute('data-open');
      document.body.classList.remove('is-locked');
      if (this.release) { this.release(); this.release = null; }
      if (openPopup === this) openPopup = null;

      /* A decline is remembered longer than an incidental close */
      Memory.suppress(this.storageKey, reason === 'declined');

      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.dispatchEvent(new CustomEvent('aki:modal:close', { bubbles: true, detail: { reason: reason } }));
    }
  }

  /* ==========================================
     <aki-email-popup>
     Opens on a timer, on scroll depth, or on exit intent —
     whichever fires first.
     ========================================== */
  class AkiEmailPopup extends AkiModal {
    connectedCallback() {
      super.connectedCallback();
      if (this._emailReady) return;
      this._emailReady = true;

      /* The form just posted successfully — show the confirmation once,
         then never ask this visitor again. */
      if (this.querySelector('[data-popup-success]')) {
        Memory.suppress(this.storageKey, true);
        this.open();
        return;
      }

      if (!this.canOpen()) return;

      this.delay = Number(this.dataset.delay || 8) * 1000;
      this.scrollPct = Number(this.dataset.scroll || 45);
      this.exitIntent = this.dataset.exitIntent === 'true';

      this.armed = true;
      this.timer = window.setTimeout(() => this.trigger(), this.delay);

      this.onScroll = () => {
        var h = document.documentElement;
        var pct = (h.scrollTop / (h.scrollHeight - h.clientHeight || 1)) * 100;
        if (pct >= this.scrollPct) this.trigger();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      if (this.exitIntent) {
        this.onLeave = (e) => { if (e.clientY <= 0) this.trigger(); };
        document.addEventListener('mouseleave', this.onLeave);
      }
    }

    trigger() {
      if (!this.armed || !this.canOpen()) return;
      this.armed = false;
      this.teardown();
      this.open();
    }

    teardown() {
      if (this.timer) window.clearTimeout(this.timer);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onLeave) document.removeEventListener('mouseleave', this.onLeave);
    }

    disconnectedCallback() { this.teardown(); }
  }

  /* ==========================================
     <aki-upsell-popup>
     Fires after a successful add-to-cart.
     Adds the same product again — a "make it a stock-up"
     offer, not a competing product.
     ========================================== */
  class AkiUpsellPopup extends AkiModal {
    connectedCallback() {
      super.connectedCallback();
      if (this._upsellReady) return;
      this._upsellReady = true;

      this.acceptBtn = this.querySelector('[data-upsell-accept]');

      if (this.acceptBtn) {
        this.acceptBtn.addEventListener('click', async (e) => {
          e.preventDefault();
          var id = Number(this.acceptBtn.dataset.variantId);
          var qty = Number(this.acceptBtn.dataset.quantity) || 1;
          if (!id || !window.AkiCart) return;

          this.acceptBtn.dataset.loading = 'true';
          try {
            await window.AkiCart.add([{ id: id, quantity: qty }]);
            this.close('accepted');
            var drawer = document.getElementById('aki-cart-drawer');
            if (drawer && typeof drawer.open === 'function') drawer.open();
          } catch (err) {
            console.error('aki: upsell add failed', err);
          } finally {
            delete this.acceptBtn.dataset.loading;
          }
        });
      }

      /* Listen for the add-to-cart success broadcast */
      document.addEventListener('aki:added-to-cart', (e) => {
        if (!document.contains(this)) return;
        /* Never offer the upsell when they already bought more than one */
        var qty = e.detail && e.detail.quantity ? e.detail.quantity : 1;
        if (qty > 1) return;
        if (!this.canOpen()) {
          this.fallbackToDrawer();
          return;
        }
        this.open();
      });
    }

    fallbackToDrawer() {
      var drawer = document.getElementById('aki-cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    close(reason) {
      var wasOpen = this.hasAttribute('data-open');
      super.close(reason);
      /* Declining should still take them to the cart */
      if (wasOpen && reason !== 'accepted') this.fallbackToDrawer();
    }
  }

  /* ==========================================
     <aki-social-proof>
     Rotates real review data injected from Liquid.
     If the element has no items, nothing ever renders.
     ========================================== */
  class AkiSocialProof extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.items = Array.from(this.querySelectorAll('[data-proof-item]'));
      if (!this.items.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.delay = Number(this.dataset.delay || 6) * 1000;
      this.duration = Number(this.dataset.duration || 6) * 1000;
      this.gap = Number(this.dataset.gap || 12) * 1000;
      this.index = 0;

      var close = this.querySelector('[data-proof-close]');
      if (close) {
        close.addEventListener('click', () => {
          this.stop();
          this.hide();
          Memory.suppress('social-proof', true);
        });
      }

      if (Memory.isSuppressed('social-proof', 30)) return;

      this.startTimer = window.setTimeout(() => this.cycle(), this.delay);
    }

    show(i) {
      this.items.forEach(function (el, n) { el.hidden = n !== i; });
      this.setAttribute('data-open', 'true');
    }

    hide() { this.removeAttribute('data-open'); }

    cycle() {
      if (document.hidden) {
        this.nextTimer = window.setTimeout(() => this.cycle(), this.gap);
        return;
      }
      this.show(this.index);
      this.index = (this.index + 1) % this.items.length;

      this.hideTimer = window.setTimeout(() => {
        this.hide();
        this.nextTimer = window.setTimeout(() => this.cycle(), this.gap);
      }, this.duration);
    }

    stop() {
      [this.startTimer, this.hideTimer, this.nextTimer].forEach(function (t) {
        if (t) window.clearTimeout(t);
      });
    }

    disconnectedCallback() { this.stop(); }
  }

  /* ==========================================
     <aki-card-game>
     Karten-Reveal.

     EHRLICHKEIT: Die Karten werden über die tatsächlich im Editor
     hinterlegten Preise gemischt. Es gibt keine gesteuerte Ziehung und
     keinen Preis, der nur so aussieht. Ist genau ein Preis hinterlegt,
     zeigt jede Karte denselben — dann ist es ein Reveal, keine Lotterie.
     ========================================== */
  class AkiCardGame extends AkiModal {
    connectedCallback() {
      super.connectedCallback();
      if (this._gameReady) return;
      this._gameReady = true;

      this.cards = Array.from(this.querySelectorAll('[data-card]'));
      this.prizes = this.readPrizes();
      if (!this.cards.length || !this.prizes.length) return;

      this.setAttribute('data-state', 'ready');
      this.dealt = false;

      this.cards.forEach((card) => {
        card.addEventListener('click', () => this.pick(card));
      });

      var copyBtn = this.querySelector('[data-game-copy]');
      if (copyBtn) copyBtn.addEventListener('click', () => this.copyCode(copyBtn));

      /* Opens on a timer unless another popup already owns the screen */
      if (!this.canOpen()) return;
      this.delay = Number(this.dataset.delay || 4) * 1000;
      this.timer = window.setTimeout(() => {
        if (this.canOpen()) { this.shuffle(); this.open(); }
      }, this.delay);
    }

    readPrizes() {
      var el = this.querySelector('[data-game-prizes]');
      if (!el) return [];
      try { return JSON.parse(el.textContent) || []; }
      catch (e) { return []; }
    }

    /* Fisher-Yates over the real configured prizes */
    shuffle() {
      var pool = this.prizes.slice();
      for (var i = pool.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var t = pool[i]; pool[i] = pool[j]; pool[j] = t;
      }
      this.dealt = this.cards.map(function (_, i) { return pool[i % pool.length]; });

      this.setAttribute('data-state', 'shuffling');
      window.setTimeout(() => {
        if (this.getAttribute('data-state') === 'shuffling') this.setAttribute('data-state', 'ready');
      }, 800);
    }

    pick(card) {
      if (this.getAttribute('data-state') !== 'ready' || !this.dealt) return;

      var index = this.cards.indexOf(card);
      var prize = this.dealt[index];
      if (!prize) return;

      this.setAttribute('data-state', 'revealing');

      var front = card.querySelector('[data-card-prize]');
      if (front) front.textContent = prize.label;

      card.setAttribute('data-picked', 'true');
      card.setAttribute('aria-expanded', 'true');
      this.cards.forEach(function (c) {
        c.disabled = true;
        if (c !== card) c.setAttribute('data-dimmed', 'true');
      });

      window.setTimeout(() => {
        this.showResult(prize);
        this.setAttribute('data-state', 'revealed');
      }, 620);
    }

    showResult(prize) {
      var titleEl = this.querySelector('[data-game-result-title]');
      var textEl = this.querySelector('[data-game-result-text]');
      var codeWrap = this.querySelector('[data-game-code-wrap]');
      var codeEl = this.querySelector('[data-game-code]');
      var ctaEl = this.querySelector('[data-game-cta]');

      if (titleEl) titleEl.textContent = prize.label;
      if (textEl) textEl.textContent = prize.text || '';

      if (prize.code) {
        if (codeEl) codeEl.textContent = prize.code;
        if (codeWrap) codeWrap.hidden = false;
        /* Hand the code to the checkout so it is applied for real */
        if (ctaEl) {
          var base = ctaEl.dataset.baseHref || ctaEl.getAttribute('href') || '/';
          ctaEl.dataset.baseHref = base;
          ctaEl.setAttribute('href', base + (base.indexOf('?') > -1 ? '&' : '?') + 'discount=' + encodeURIComponent(prize.code));
        }
      } else if (codeWrap) {
        codeWrap.hidden = true;
      }

      Memory.suppress(this.storageKey, true);
    }

    copyCode(button) {
      var codeEl = this.querySelector('[data-game-code]');
      if (!codeEl || !navigator.clipboard) return;
      navigator.clipboard.writeText(codeEl.textContent.trim()).then(function () {
        var original = button.dataset.label || button.textContent;
        button.dataset.label = original;
        button.dataset.copied = 'true';
        button.textContent = button.dataset.copiedLabel || 'Kopiert';
        window.setTimeout(function () {
          button.textContent = original;
          delete button.dataset.copied;
        }, 1800);
      }).catch(function () { /* clipboard blocked — the code is visible anyway */ });
    }

    disconnectedCallback() {
      if (this.timer) window.clearTimeout(this.timer);
    }
  }

  /* ==========================================
     Registration
     ========================================== */
  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  define('aki-modal', AkiModal);
  define('aki-email-popup', AkiEmailPopup);
  define('aki-upsell-popup', AkiUpsellPopup);
  define('aki-social-proof', AkiSocialProof);
  define('aki-card-game', AkiCardGame);
})();
